import { useFormik, type FormikErrors } from 'formik';

import { NotificationType, useNotifications } from 'modules/app/notifications';
import isValidEmail from 'src/auth/utils/isValidEmail';
import { PanelItemsSection } from 'src/core/common/components/Panel';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { COUNTRIES } from 'src/core/config/country';

import { useUpdateSupplierDetailsMutation } from '../../../hooks/useUpdateSupplierDetailsMutation';
import {
  type LegalDetailsFormValues,
  type SupplierDetails,
} from '../../../types';
import { AccountPayableSuppliersPanelLegalDetailsForm } from '../AccountPayableSuppliersPanelLegalDetailsForm';

type LegalDetailsFormValuesWithPrimaryEmail = LegalDetailsFormValues & {
  primaryEmail: string;
};

type Props = {
  supplier: SupplierDetails;
};

export const AccountPayableSuppliersPanelLegalDetailsSection = ({
  supplier,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}: Props) => {
  const { t } = useTranslation('global');

  const { pushNotif } = useNotifications();

  const [updateSupplierDetailsMutation] = useUpdateSupplierDetailsMutation();

  const initialValues: LegalDetailsFormValuesWithPrimaryEmail = {
    legalName: supplier.legalDetails?.legalName ?? '',
    registrationNumber: supplier.legalDetails?.registrationNumber ?? '',
    vatNumber: supplier.legalDetails?.vatNumber ?? '',
    primaryEmail: supplier.primaryEmail ?? '',
    addressCountry: supplier.legalDetails?.addressCountry ?? '',
    postalCode: supplier.legalDetails?.postalCode ?? '',
    streetAddress: supplier.legalDetails?.streetAddress ?? '',
    addressLocality: supplier.legalDetails?.addressLocality ?? '',
  };

  const formikProps = useFormik<LegalDetailsFormValuesWithPrimaryEmail>({
    enableReinitialize: true,
    initialValues,
    validateOnChange: false,
    validate: (
      values: LegalDetailsFormValuesWithPrimaryEmail,
    ): FormikErrors<LegalDetailsFormValuesWithPrimaryEmail> => {
      const errors: FormikErrors<LegalDetailsFormValuesWithPrimaryEmail> = {};

      if (
        values.primaryEmail.trim() !== '' &&
        !isValidEmail(values.primaryEmail.trim())
      ) {
        errors.primaryEmail =
          'bookkeep.accountsPayable.panel.legalDetailsSection.primaryEmailError';
      }

      return errors;
    },
    onSubmit: async (values: LegalDetailsFormValuesWithPrimaryEmail) => {
      const { primaryEmail, ...legalDetailsValues } = values;
      const updateSupplierDetailsMutationResult =
        await updateSupplierDetailsMutation({
          supplierId: supplier.id,
          primaryEmail: primaryEmail || null,
          bankDetails: {
            bankCountry: supplier.bankDetails?.bankCountry,
          },
          legalDetails: legalDetailsValues,
        });

      if (updateSupplierDetailsMutationResult.updateSupplierDetails.reason) {
        pushNotif({
          type: NotificationType.Danger,
          message: t(
            'bookkeep.accountsPayable.panel.legalDetailsSection.updateErrorNotification',
          ),
        });
      } else {
        pushNotif({
          type: NotificationType.Success,
          message: t(
            'bookkeep.accountsPayable.panel.legalDetailsSection.updateSuccessNotification',
          ),
        });
      }
    },
  });

  return (
    <PanelItemsSection
      title={t('bookkeep.accountsPayable.panel.legalDetailsSection.title')}
      items={[
        {
          label: t(
            'bookkeep.accountsPayable.panel.legalDetailsSection.legalNameLabel',
          ),
          value: supplier.legalDetails?.legalName || '-',
        },
        {
          label: t(
            'bookkeep.accountsPayable.panel.legalDetailsSection.registrationNumberLabel',
          ),
          value: supplier.legalDetails?.registrationNumber || '-',
        },
        {
          label: t(
            'bookkeep.accountsPayable.panel.legalDetailsSection.vatNumberLabel',
          ),
          value: supplier.legalDetails?.vatNumber || '-',
        },
        {
          label: t(
            'bookkeep.accountsPayable.panel.legalDetailsSection.companyAddressLabel',
          ),
          value: supplier.legalDetails?.streetAddress
            ? `${supplier.legalDetails?.streetAddress}, ${
                supplier.legalDetails?.postalCode ?? ''
              } ${supplier.legalDetails?.addressLocality ?? ''}`
            : '-',
        },
        {
          label: t(
            'bookkeep.accountsPayable.panel.legalDetailsSection.supplierCountryLabel',
          ),
          value:
            supplier.legalDetails?.addressCountry &&
            COUNTRIES[supplier.legalDetails?.addressCountry]
              ? t(
                  COUNTRIES[supplier.legalDetails?.addressCountry]
                    .translationKey,
                )
              : '-',
        },
        {
          label: t(
            'bookkeep.accountsPayable.panel.legalDetailsSection.primaryEmailLabel',
          ),
          value: supplier.primaryEmail || '-',
        },
      ]}
      isEditable
      cancelTranslation={t('misc.cancel')}
      saveTranslation={t('misc.saveChanges')}
      editSection={
        <AccountPayableSuppliersPanelLegalDetailsForm {...formikProps} />
      }
      onCancel={() => {
        formikProps.resetForm();
      }}
      onSave={async () => {
        // this will trigger 2 form validations
        // it is due to a formik bug that doesn't reject the promise when calling submitForm and there are validation errors
        // https://github.com/formium/formik/issues/1580
        const { submitForm, validateForm } = formikProps;
        const errors = await validateForm();
        const isValid = Object.keys(errors).length === 0;
        if (!isValid) {
          throw new Error('Validation error');
        }
        return submitForm();
      }}
    />
  );
};
